import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui-hud";
import { CheckCircle2, XCircle, Loader2, FileText, ExternalLink, Clock, ShieldCheck } from "lucide-react";

const STATUSES = ["pending", "approved", "rejected"];

/**
 * Admin FA Verification Queue — reachable at /admin/fa-verifications.
 * Lists submitted FA documents with quick-approve / quick-reject actions.
 * Used inside the /admin router tab as well.
 */
export default function AdminFaVerifications() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/fa-verifications?status=${status}`)
      .then((r) => setRows(r.data.submissions || []))
      .catch(() => toast.error("Failed to load queue"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const decide = async (id, approve) => {
    setBusy(id);
    try {
      await api.post(`/admin/fa-verifications/${id}/review`, {
        approve,
        review_note: note[id] || null,
      });
      toast.success(approve ? "Approved" : "Rejected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Review failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-fa-verifications">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-cyan mb-1">// FLIGHT ATTENDANT · VERIFICATION QUEUE</div>
          <h2 className="font-display text-2xl font-bold">Manual verifications</h2>
        </div>
        <div className="flex gap-2" data-testid="fa-queue-status-tabs">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`fa-queue-tab-${s}`}
              className={`px-3 py-1.5 rounded-full text-[11px] tracking-widest uppercase mono border transition-colors ${status === s ? "border-cyan text-cyan bg-cyan/[0.08]" : "border-white/10 text-white/50 hover:text-white"}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-white/50 text-sm py-6"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-white/50 text-sm py-10 text-center border border-white/10 border-dashed rounded-lg">
          No <b className="text-white">{status}</b> submissions.
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r) => (
          <Card key={r.id} className="!p-4" testId={`fa-verify-row-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="font-display text-lg font-semibold text-white truncate">{r.user_name || "Unnamed"}</div>
                <div className="text-white/55 text-xs truncate">{r.user_email}</div>
              </div>
              <div className="mono text-[10px] tracking-widest uppercase text-white/45 flex items-center gap-2">
                {r.status === "pending" && <Clock className="h-3 w-3 text-cyan animate-pulse" />}
                {r.status === "approved" && <ShieldCheck className="h-3 w-3 text-avail" />}
                {r.status === "rejected" && <XCircle className="h-3 w-3 text-red-400" />}
                {r.status}
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mb-3 text-sm">
              <div>
                <div className="mono text-[10px] tracking-widest uppercase text-white/45 mb-1">Employer</div>
                <div className="text-white/85">{r.employer_name}</div>
              </div>
              <div>
                <div className="mono text-[10px] tracking-widest uppercase text-white/45 mb-1">Doc type</div>
                <div className="text-white/85">{r.doc_type}</div>
              </div>
              <div>
                <div className="mono text-[10px] tracking-widest uppercase text-white/45 mb-1">Submitted</div>
                <div className="text-white/85">{new Date(r.created_at).toLocaleString()}</div>
              </div>
            </div>
            {r.note && <div className="text-sm text-white/70 mb-3"><b className="text-white/50">Note from crew:</b> {r.note}</div>}
            <div className="flex items-center gap-3 mb-3">
              <a href={`/api/files/${r.file_id}`} target="_blank" rel="noopener noreferrer" className="btn-hud" data-testid={`fa-verify-open-doc-${r.id}`}>
                <FileText className="h-3.5 w-3.5" /> Open document <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            {r.status === "pending" && (
              <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/[0.06]">
                <input
                  type="text"
                  placeholder="Review note (optional, shown to crew if rejected)"
                  className="input-hud flex-1 min-w-[220px]"
                  value={note[r.id] || ""}
                  onChange={(e) => setNote({ ...note, [r.id]: e.target.value })}
                  data-testid={`fa-verify-note-${r.id}`}
                />
                <button
                  onClick={() => decide(r.id, true)}
                  disabled={busy === r.id}
                  data-testid={`fa-verify-approve-${r.id}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-avail text-obsidian text-sm font-semibold hover:brightness-110 disabled:opacity-60"
                >
                  {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="h-4 w-4" /> Approve</>}
                </button>
                <button
                  onClick={() => decide(r.id, false)}
                  disabled={busy === r.id}
                  data-testid={`fa-verify-reject-${r.id}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-500/40 text-red-400 text-sm hover:bg-red-500/10 disabled:opacity-60"
                >
                  <XCircle className="h-4 w-4" /> Reject
                </button>
              </div>
            )}
            {r.status !== "pending" && r.review_note && (
              <div className="mt-2 text-sm text-white/60"><b>Reviewer note:</b> {r.review_note}</div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
